/**
 * `wayai pull` / `wayai push` against the `bases/` subtree.
 *
 * The hub half of each verb is unchanged in `commands/{pull,push}.ts`; this is
 * the base half those commands hand off to once the subtree router has decided
 * (docs/reference/rekor-platform-contracts.md §3.3). Both halves keep their own
 * per-subtree rules: `push` here targets PREVIEW bases only, exactly as `push`
 * there targets preview hubs only — production is `wayai bases promote` on this
 * side and `wayai publish` on that one.
 *
 * Reached only through the `/v1` proxy: one keyring login, one `api_url`, and
 * the org attested by the backend over its service binding.
 */

import * as path from 'node:path';
import { createDataClient, type DataClient } from '../client.js';
import { confirm } from '../../lib/utils.js';
import { expected } from '../../lib/expected.js';
import { resolveBasesDir } from '../../lib/layout.js';
import { sanitizeTerminalText } from '../../lib/terminal-output.js';
import { isPathSafeId } from '../../lib/base-id.js';
import { printLocalFileChanges, printWarnings } from '../../commands/push.js';
import {
  assertInScope,
  assertScopeAllowsCreation,
  seedScopeIfEmpty,
} from '../../lib/worktree-scope.js';
import {
  applyConfig,
  createPreview,
  deleteEntity,
  diffConfig,
  getBaseRecord,
  getConfig,
  metaFromRecord,
  type BaseRecord,
} from './api.js';
import { parseBaseFolder } from './config-parser.js';
import { ensureMirrorIgnored, markProductionMirror, writeBaseFolder } from './config-writer.js';
import { RED, RESET, YELLOW, collectRemovals, isDiffEmpty, renderDiff } from './diff-format.js';
import {
  isProductionFolder,
  resolveBaseTarget,
  targetBaseId,
  type ResolvedBaseTarget,
} from '../../lib/base-workspace.js';
import { ENTITY_KINDS, type LintWarning } from './types.js';

interface BaseSyncArgs {
  autoConfirm: boolean;
  dryRun: boolean;
  prune: boolean;
}

/**
 * The flags this side reads. `--base` and the positional are already consumed by
 * the subtree router, which passes the selector it resolved.
 */
function parseArgs(args: string[]): BaseSyncArgs {
  return {
    autoConfirm: args.includes('--yes') || args.includes('-y'),
    dryRun: args.includes('--dry-run'),
    prune: args.includes('--prune'),
  };
}

/** The git root the caller already resolved, or the failure every workspace command gives. */
function requireGitRoot(gitRoot: string | null): string {
  if (!gitRoot) throw expected('Not inside a git repository.');
  return gitRoot;
}

/**
 * Materialize a production base as a READ-ONLY mirror so the live config is
 * browsable beside the preview that promotes into it. Never binds the worktree —
 * a mirror is not a push target — and never seeds an editable scaffold.
 */
async function writeProductionMirror(
  client: DataClient,
  basesDir: string,
  prodId: string,
  record: BaseRecord,
): Promise<string> {
  // `prodId` is server-provided and becomes a filesystem path segment below.
  if (!isPathSafeId(prodId)) {
    throw expected(`Refusing to mirror base ${JSON.stringify(prodId)}: not a usable base id.`);
  }
  const config = await getConfig(client, prodId);
  const folder = path.join(basesDir, prodId);
  // Production is confirmed by the caller; hard-set it so the read-only
  // discriminator holds even if the record's own field were ever absent.
  writeBaseFolder(folder, { ...metaFromRecord(record, prodId), environment: 'production' }, config);
  markProductionMirror(folder, prodId);
  ensureMirrorIgnored(folder);
  return folder;
}

/**
 * Mirror a preview's linked ORIGIN when that origin is production. Best-effort:
 * the preview pull has already succeeded, so a mirror failure must not fail the
 * command — the same posture as the hub side's linked-production mirror.
 *
 * An origin may itself be a preview, in which case there is no mirror to write:
 * rewriting the user's own working folder to `environment: production` would
 * prepend the read-only banner and drop a `*` .gitignore over a folder they edit.
 */
async function mirrorLinkedProduction(
  client: DataClient,
  basesDir: string,
  originId: string,
): Promise<void> {
  try {
    const record = await getBaseRecord(client, originId);
    if (record.environment !== 'production') return;
    const folder = await writeProductionMirror(client, basesDir, originId, record);
    console.log(`Mirrored production base → ${folder} (read-only, git-ignored)`);
  } catch (err) {
    console.warn(
      `Warning: could not mirror the production base (${err instanceof Error ? err.message : String(err)}). The preview pull is unaffected.`,
    );
  }
}

/** `wayai pull` against a base: server config → workspace files. */
export async function pullBase(
  gitRootOrNull: string | null,
  selector: string | undefined,
  args: string[],
): Promise<void> {
  const { autoConfirm } = parseArgs(args);
  const gitRoot = requireGitRoot(gitRootOrNull);
  const target = resolveBaseTarget(gitRoot, selector);
  // Mirrors always land under the resolved bases dir, never beside a target the
  // caller reached by an out-of-tree path.
  const basesDir = resolveBasesDir(gitRoot);
  const baseId = targetBaseId(target, selector);
  if (!baseId) {
    throw expected(
      `Could not determine the base id for ${target.folder}. Pass the base id explicitly.`,
    );
  }

  const client = await createDataClient();
  console.log('Fetching base configuration...');
  const record = await getBaseRecord(client, baseId);

  if (record.environment === 'production') {
    // A directly-targeted production base materializes as a read-only mirror:
    // not editable, not scoped, and always safe to refresh — so this returns
    // before the scope guard and the overwrite confirmation.
    const folder = await writeProductionMirror(client, basesDir, record.id ?? baseId, record);
    console.log(`Production base mirrored (read-only) → ${folder}`);
    return;
  }

  // Preview base → enforce the worktree scope before writing editable source.
  assertInScope('bases', baseId);

  if (target.exists && !autoConfirm) {
    const ok = await confirm(
      `Overwrite local files in ${path.relative(gitRoot, target.folder)} with the server config for "${sanitizeTerminalText(baseId)}"?`,
    );
    if (!ok) {
      console.log('Cancelled.');
      return;
    }
  }

  const config = await getConfig(client, baseId);
  const isFirstPull = !target.exists;
  const delta = writeBaseFolder(target.folder, metaFromRecord(record, baseId), config);

  console.log(`Base configuration written to ${target.folder}`);
  console.log(
    `  ${ENTITY_KINDS.map((kind) => `${(config[kind] ?? []).length} ${kind}`).join(', ')}`,
  );
  // Name the files that changed so nothing is committed unread — but stay quiet
  // on a brand-new folder, where every file is new and a full listing is noise.
  // Same rule the hub side applies to its own first pull.
  if (!isFirstPull) printLocalFileChanges(delta);
  console.log('  Credential values are not written. Manage them with `wayai bases credentials`.');

  seedScopeIfEmpty('bases', baseId);

  if (record.origin_base_id) {
    await mirrorLinkedProduction(client, basesDir, record.origin_base_id);
  }
}

/** Advisory tool-design warnings. Printed to stderr so machine-readable stdout stays clean. */
function printLintWarnings(warnings: LintWarning[] | undefined): void {
  if (!warnings || warnings.length === 0) return;
  const word = warnings.length === 1 ? 'suggestion' : 'suggestions';
  console.error(`\n${YELLOW}Tool design — ${warnings.length} ${word} (advisory, not blocking):${RESET}`);
  for (const w of warnings) {
    console.error(
      `  ${sanitizeTerminalText(w.action)} [${sanitizeTerminalText(w.rule)}]\n    ${sanitizeTerminalText(w.message)}`,
    );
  }
}

/**
 * Create the preview this folder declares but has not yet been given an id for,
 * and write back what the SERVER reports. `base.yaml` is a committed assertion
 * about the base, and `analytics` in particular can never be corrected
 * afterwards, so it must describe what was actually created.
 */
async function createDeclaredPreview(
  client: DataClient,
  target: ResolvedBaseTarget,
  parsed: ReturnType<typeof parseBaseFolder>,
  autoConfirm: boolean,
): Promise<string | null> {
  const { meta, config } = parsed;
  if (!meta.origin_base_id) {
    throw expected(
      'base.yaml needs a base_id (an existing preview) or an origin_base_id (to create one).',
    );
  }
  const name = meta.name ?? path.basename(target.folder);
  // Routing guard: don't spin up a new preview in a worktree scoped to other bases.
  assertScopeAllowsCreation('bases', name);
  if (!autoConfirm) {
    // "origin", not "production": a preview may be cloned from another preview.
    const ok = await confirm(
      `Create a new preview "${sanitizeTerminalText(name)}" from "${sanitizeTerminalText(meta.origin_base_id)}"?`,
    );
    if (!ok) {
      console.log('Cancelled.');
      return null;
    }
  }
  const record = await createPreview(client, meta.origin_base_id, {
    name,
    description: meta.description,
    analytics: meta.analytics,
  });
  const id = record.id;
  writeBaseFolder(target.folder, { ...meta, ...metaFromRecord(record, id) }, config);
  console.log(`Created preview "${sanitizeTerminalText(id)}".`);
  // Same yellow block push already renders for its own advisories; the messages
  // are server text, so they are sanitized on the way in.
  printWarnings((record.warnings ?? []).map((w) => sanitizeTerminalText(w.message)));
  // The preview exists server-side even on a --dry-run push, so scope now.
  seedScopeIfEmpty('bases', id);
  return id;
}

/** `wayai push` against a base: workspace files → preview base. */
export async function pushBase(
  gitRootOrNull: string | null,
  selector: string | undefined,
  args: string[],
): Promise<void> {
  const { autoConfirm, dryRun, prune } = parseArgs(args);
  const gitRoot = requireGitRoot(gitRootOrNull);
  const target = resolveBaseTarget(gitRoot, selector);
  if (!target.exists) {
    throw expected(
      `No base.yaml in ${target.folder}. Pull an existing preview, or scaffold a folder with origin_base_id set to create one.`,
    );
  }

  const parsed = parseBaseFolder(target.folder);
  const { meta, config } = parsed;

  // Refuse a read-only production mirror BEFORE any network round-trip. The
  // backend refuses it too, but only after a call — and §3.3 places every
  // refusal ahead of the first request.
  if (isProductionFolder(meta)) {
    throw expected(
      `${path.relative(gitRoot, target.folder)} is a read-only production mirror. Push operates on previews — edit the linked preview and promote with \`wayai bases promote\`.`,
    );
  }

  // A folder with no `base_id` has nothing on the server to diff against, so the
  // only way to show a diff would be to CREATE the preview first — which
  // `--dry-run` promises not to do. Creating is irreversible in the way that
  // matters: the analytics tier is fixed at creation, the worktree gets scoped,
  // and `base.yaml` is rewritten, so a CI pipeline re-running the same dry-run
  // would mint a fresh orphan base every time. Stop here instead, before any
  // request, and say what a real push would do.
  if (!meta.base_id && dryRun) {
    console.log(
      `Dry run — nothing applied. ${path.relative(gitRoot, target.folder)} has no base_id yet, so a real push would first create a preview from "${sanitizeTerminalText(meta.origin_base_id ?? '<origin_base_id>')}".`,
    );
    return;
  }

  const client = await createDataClient();

  let baseId = meta.base_id;
  if (!baseId) {
    const created = await createDeclaredPreview(client, target, parsed, autoConfirm);
    if (!created) return;
    baseId = created;
  } else {
    const record = await getBaseRecord(client, baseId);
    if (record.environment && record.environment !== 'preview') {
      throw expected(
        `"${sanitizeTerminalText(baseId)}" is a ${sanitizeTerminalText(record.environment)} base. Push operates on previews — promote with \`wayai bases promote\`.`,
      );
    }
    // Routing guard: fail before diffing or applying against the wrong preview.
    assertInScope('bases', baseId);
  }

  const diff = await diffConfig(client, baseId, config);
  // Printed BEFORE the empty-diff return: a config pushed before these rules
  // existed is exactly the one that never changes again, so a no-op push is the
  // only chance to surface them.
  printLintWarnings(diff.warnings);

  if (isDiffEmpty(diff)) {
    console.log(`No changes — preview "${sanitizeTerminalText(baseId)}" already matches your files.`);
    // A no-op push still confirms this worktree targets the base. Skipped on
    // --dry-run, which stays side-effect-free.
    if (!dryRun) seedScopeIfEmpty('bases', baseId);
    return;
  }

  console.log(renderDiff(diff));

  if (dryRun) {
    console.log('\nDry run — nothing applied.');
    return;
  }

  if (!autoConfirm) {
    const ok = await confirm(`\nApply these changes to preview "${sanitizeTerminalText(baseId)}"?`);
    if (!ok) {
      console.log('Cancelled.');
      return;
    }
  }

  const result = await applyConfig(client, baseId, config);

  // Freshly generated secrets for added triggers / inbound webhooks — shown ONCE
  // and never written to disk (the writer's allowlist keeps `secret` off it), so
  // losing one means deleting and recreating the entity.
  //
  // Printed per namespace rather than merged: `triggers/` and `inbound-webhooks/`
  // are separate id spaces, so a base may legitimately declare `orders` in both,
  // and a spread into one object would drop whichever came first. Labelling also
  // answers the question a bare id cannot — which entity the secret is for.
  const secretGroups: Array<[string, Record<string, string> | undefined]> = [
    ['trigger', result.new_trigger_secrets],
    ['inbound webhook', result.new_inbound_webhook_secrets],
  ];
  if (secretGroups.some(([, map]) => map && Object.keys(map).length > 0)) {
    console.log(`\n${YELLOW}New secrets (shown once — save them now):${RESET}`);
    for (const [kind, map] of secretGroups) {
      for (const [id, secret] of Object.entries(map ?? {})) {
        console.log(`  ${kind} ${sanitizeTerminalText(id)}: ${secret}`);
      }
    }
  }

  // Deletions are opt-in (record-type deletes cascade to records) and separately confirmed.
  const removals = collectRemovals(diff);
  if (removals.length > 0) {
    const word = removals.length === 1 ? 'entity' : 'entities';
    if (!prune) {
      console.log(
        `\n${RED}${removals.length} ${word} not in your files were left on the base. Re-run with --prune to delete.${RESET}`,
      );
    } else if (
      autoConfirm ||
      (await confirm(
        `\nDelete ${removals.length} server ${word} not in your files? Record types cascade to their records.`,
      ))
    ) {
      for (const removal of removals) {
        await deleteEntity(client, baseId, removal.kind, removal.id);
        console.log(`  - deleted ${removal.kind} ${sanitizeTerminalText(removal.id)}`);
      }
    } else {
      console.log('Skipped deletions.');
    }
  }

  console.log(`\nPushed to preview "${sanitizeTerminalText(baseId)}".`);
  seedScopeIfEmpty('bases', baseId);
}
